import { Address } from "@/models/address/address"
import { IAddress } from "@/models/address/address.types"
import { IImbpAccount, IImbpMeterIdentifier } from "./imbpAccount.types"
import { postcodeFormatter } from "@/helpers/postcodeFormatterHelper"
import { IContact } from "../contacts/contact.types"
import { IMeterpoint } from "../meterpoint/meterpoint.types"

export class ImbpAccount implements IImbpAccount {
  billingAccountId: number | null
  contactInfo?: IContact
  address?: IAddress
  meterpoints?: IMeterpoint[]

  constructor(
    billingAccountId: number | null,
    contactInfo?: IContact,
    address?: IAddress,
    meterpoints?: IMeterpoint[],
  ) {
    this.contactInfo = contactInfo
    this.address = address
      ? new Address(
          address.countryCode,
          address.postcode,
          address.addressLine1,
          address.addressLine2,
          address.addressLine3,
        )
      : undefined
    this.billingAccountId = billingAccountId
    this.meterpoints = meterpoints
  }

  get formattedName() {
    const fullName = `${this?.contactInfo?.firstName} ${this?.contactInfo?.lastName}`
    return this.contactInfo?.title
      ? `${this.contactInfo.title} ${fullName}`
      : fullName
  }

  get postcode() {
    return this.address?.postcode
  }

  get formattedPostcode() {
    return postcodeFormatter(this.address?.postcode ?? "")
  }

  get customerAddress() {
    return [this.address?.addressLine1, this.address?.addressLine2]
      .filter(Boolean)
      .join(", ")
  }

  get electricMeter() {
    const elecMeterpoints = this.meterpoints?.filter(
      (meterpoint) => meterpoint.isElectricity,
    )
    if (!elecMeterpoints?.length) return null

    return elecMeterpoints?.reduce((meterData, meterpoint) => {
      const meterSerialNumber = meterpoint.meters?.find(
        (meter) => meter.identifier,
      )

      if (meterSerialNumber?.identifier && meterpoint.identifier) {
        meterData = {
          id: meterpoint.identifier,
          serialNumber: meterSerialNumber.identifier,
        }
      }
      return meterData
    }, {} as IImbpMeterIdentifier)
  }

  get gasMeter() {
    const gasMeterpoints = this.meterpoints?.filter(
      (meterpoint) => !meterpoint.isElectricity,
    )
    if (!gasMeterpoints?.length) return null

    return gasMeterpoints?.reduce((meterData, meterpoint) => {
      const meterSerialNumber = meterpoint.meters?.find(
        (meter) => meter.identifier,
      )

      if (meterSerialNumber?.identifier && meterpoint.identifier) {
        meterData = {
          id: meterpoint.identifier,
          serialNumber: meterSerialNumber.identifier,
        }
      }
      return meterData
    }, {} as IImbpMeterIdentifier)
  }

  get mpan() {
    return this.electricMeter?.id?.toString() ?? ""
  }

  get mprn() {
    return this.gasMeter?.id?.toString() ?? ""
  }

  get gasMeterSerialNumber() {
    return this.gasMeter?.serialNumber?.toString() ?? ""
  }

  get electricityMeterSerialNumber() {
    return this.electricMeter?.serialNumber?.toString() ?? ""
  }
}
